import api from "./api/index";
import { getApiUrl } from "./config";
import { endpoint } from "./endpoints";
import { handleApiCall } from "../utils/apiResponseHandler";
import { A2PCheckoutSession, A2PPayment } from "@/app/_module/types/a2p.types";

/**
 * A2P Payment Service
 * Handles payment operations for A2P verification fees
 */
class A2PPaymentService {
  /**
   * Create Stripe checkout session for A2P payment
   * @returns Checkout session with URL to redirect user to Stripe
   */
  async createCheckoutSession(): Promise<A2PCheckoutSession> {
    try {
      const result = await handleApiCall<{
        statusCode: number;
        message: string;
        data: A2PCheckoutSession;
      }>(async () => {
        const response = await api.post<{
          statusCode: number;
          message: string;
          data: A2PCheckoutSession;
        }>({
          url: getApiUrl(endpoint.communication.a2p.paymentCreateSession),
          body: {},
          auth: true
        });
        return response;
      });

      if (result.statusCode === 200 || result.statusCode === 201) {
        return result.data;
      }

      throw new Error(result.message || 'Failed to create checkout session');
    } catch (error: any) {

      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Failed to create payment checkout session'
      );
    }
  }

  /**
   * Check payment status by payment ID
   * @param paymentId - The payment ID to check
   * @returns Payment status details
   */
  async checkPaymentStatus(paymentId: string): Promise<A2PPayment> {
    try {
      const result = await handleApiCall<{
        statusCode: number;
        message: string;
        data: A2PPayment;
      }>(async () => {
        const response = await api.get<{
          statusCode: number;
          message: string;
          data: A2PPayment;
        }>({
          url: getApiUrl(endpoint.communication.a2p.paymentStatus(paymentId)),
          auth: true
        });
        return response;
      });

      if (result.statusCode === 200) {
        return result.data;
      }

      throw new Error(result.message || 'Failed to check payment status');
    } catch (error: any) {

      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Failed to check payment status'
      );
    }
  }

  /**
   * Check if payment is required for A2P registration
   * Platform covers first attempt for ALL tiers - payment only required on retry after rejection
   * @returns Payment requirement status
   */
  async checkPaymentRequired(): Promise<{ paymentRequired: boolean; hasRejectedVerification: boolean; coveredByPlatform: boolean; message: string }> {
    try {
      const result = await handleApiCall<{
        statusCode: number;
        message: string;
        data: { paymentRequired: boolean; hasRejectedVerification: boolean; coveredByPlatform: boolean; message: string };
      }>(async () => {
        const response = await api.get<{
          statusCode: number;
          message: string;
          data: { paymentRequired: boolean; hasRejectedVerification: boolean; coveredByPlatform: boolean; message: string };
        }>({
          url: getApiUrl(endpoint.communication.a2p.paymentCheck),
          auth: true
        });
        return response;
      });

      if (result.statusCode === 200) {
        return result.data;
      }

      throw new Error(result.message || 'Failed to check payment requirement');
    } catch (error: any) {
      // Default to requiring payment if check fails (safer fallback)
      return {
        paymentRequired: false,
        hasRejectedVerification: false,
        coveredByPlatform: true,
        message: 'Unable to verify payment status - defaulting to platform coverage'
      };
    }
  }

  /**
   * Get payment by verification ID
   * @param verificationId - The verification ID to look up payment for
   * @returns Payment details if found, null otherwise
   */
  async getPaymentByVerification(verificationId: string): Promise<A2PPayment | null> {
    try {
      const result = await handleApiCall<{
        statusCode: number;
        message: string;
        data: A2PPayment | null;
      }>(async () => {
        const response = await api.get<{
          statusCode: number;
          message: string;
          data: A2PPayment | null;
        }>({
          url: getApiUrl(endpoint.communication.a2p.paymentByVerification(verificationId)),
          auth: true
        });
        return response;
      });

      if (result.statusCode === 200) {
        return result.data;
      }

      if (result.statusCode === 404) {
        return null;
      }

      throw new Error(result.message || 'Failed to get payment by verification');
    } catch (error: any) {

      // Return null for 404 errors (payment not found)
      if (error.response?.status === 404) {
        return null;
      }

      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Failed to get payment by verification'
      );
    }
  }
}

export const a2pPaymentService = new A2PPaymentService();
export default a2pPaymentService;
